import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'
import { contagemDe } from '@/lib/camadasGestao'
import { rotuloDoLocal } from '@/lib/tooltipsGestao'

/*
 * O histórico da Área do Gestor: quem fez, quando e o que mudou, para cada
 * mudança confirmada, entrada e recusa. É estado de servidor, então vive no
 * cache do TanStack Query, e o recorte (aba, pessoa, dia, ordem) vive na URL
 * da tela: a chave da consulta é o próprio recorte, e recarregar a página ou
 * mandar o link refaz a mesma lista. O filtro roda na API, porque o histórico
 * cresce a cada entrada na área e porque o que cada cargo vê é decisão do
 * servidor, nunca da tela.
 */

/** As abas que gravam no histórico, na ordem em que o filtro as lista. */
export const ABAS_DO_HISTORICO = ['sessao', 'acessos', 'cargos', 'camadas', 'conteudos'] as const

export type AbaDoHistorico = (typeof ABAS_DO_HISTORICO)[number]

/** Um registro de `GET /v1/admin/historico`. */
export interface ItemDoHistorico {
  id: number
  ocorridoEm: string
  /** Nulo quando não havia sessão (entrada recusada de e-mail desconhecido). */
  autor: { nome: string; cargo: string } | null
  aba: AbaDoHistorico
  acao: string
  alvoId: string | null
  antes: Record<string, unknown> | null
  depois: Record<string, unknown> | null
  /** Só o administrador, e só na edição com estado anterior, restaura. */
  restauravel: boolean
}

/** O corpo de `GET /v1/admin/historico`. */
export interface PaginaDoHistorico {
  itens: ItemDoHistorico[]
  /** Quantos registros o recorte tem, somadas todas as páginas. */
  total: number
  pagina: number
  porPagina: number
  /** As abas que o cargo da sessão enxerga: as opções do filtro de aba. */
  abasVisiveis: AbaDoHistorico[]
  podeRestaurar: boolean
}

/** O recorte da tela. Chave ausente é filtro desligado; `ordem` ausente é "mais recente". */
export interface FiltrosDoHistorico {
  aba?: AbaDoHistorico
  pessoa?: string
  /** O dia civil, `AAAA-MM-DD`. */
  dia?: string
  ordem?: 'antiga'
}

const ROTULOS_DA_ABA: Record<AbaDoHistorico, string> = {
  sessao: 'Entradas',
  acessos: 'Gestão de acessos',
  cargos: 'Cargos',
  camadas: 'Camadas',
  conteudos: 'Conteúdos',
}

export function rotuloDaAba(aba: AbaDoHistorico): string {
  return ROTULOS_DA_ABA[aba]
}

/** O limite de `pessoa` na API: 1 a 80 caracteres. */
const LIMITE_DA_PESSOA = 80

function diaValido(texto: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(texto)) return false
  const data = new Date(`${texto}T00:00:00Z`)
  return !Number.isNaN(data.getTime()) && data.toISOString().startsWith(texto)
}

/**
 * Lê o recorte da URL. Valor que a API recusaria (aba que não existe, dia fora
 * do formato ou do calendário, pessoa vazia ou longa demais) é descartado em
 * vez de repassado: a tela nunca cai num erro 400 por causa de um link velho.
 */
export function filtrosDaBusca(busca: Record<string, unknown>): FiltrosDoHistorico {
  const filtros: FiltrosDoHistorico = {}
  const { aba, pessoa, dia, ordem } = busca
  if (typeof aba === 'string' && (ABAS_DO_HISTORICO as readonly string[]).includes(aba)) {
    filtros.aba = aba as AbaDoHistorico
  }
  if (typeof pessoa === 'string') {
    const nome = pessoa.trim()
    if (nome.length > 0 && nome.length <= LIMITE_DA_PESSOA) filtros.pessoa = nome
  }
  if (typeof dia === 'string' && diaValido(dia)) filtros.dia = dia
  if (ordem === 'antiga') filtros.ordem = 'antiga'
  return filtros
}

/** A query string de uma página: só os filtros postos, e `pagina` por último. */
export function paramsDoHistorico(filtros: FiltrosDoHistorico, pagina: number): string {
  const params = new URLSearchParams()
  if (filtros.aba !== undefined) params.set('aba', filtros.aba)
  if (filtros.pessoa !== undefined) params.set('pessoa', filtros.pessoa)
  if (filtros.dia !== undefined) params.set('dia', filtros.dia)
  if (filtros.ordem !== undefined) params.set('ordem', filtros.ordem)
  params.set('pagina', String(pagina))
  return `?${params.toString()}`
}

/** O recorte tem algum filtro ligado? Define qual frase a lista vazia diz. */
export function temFiltro(filtros: FiltrosDoHistorico): boolean {
  return filtros.aba !== undefined || filtros.pessoa !== undefined || filtros.dia !== undefined
}

/**
 * A lista do recorte, página a página: "Mostrar mais registros" pede a próxima
 * e acrescenta, sem recarregar o que já está na tela. Sem nova tentativa: erro
 * de servidor deve aparecer logo, com o botão "Tentar de novo".
 */
export function useHistorico(filtros: FiltrosDoHistorico) {
  return useInfiniteQuery({
    queryKey: ['admin', 'historico', filtros] as const,
    queryFn: ({ pageParam }) =>
      apiFetch<PaginaDoHistorico>(`/v1/admin/historico${paramsDoHistorico(filtros, pageParam)}`),
    initialPageParam: 0,
    getNextPageParam: (ultima, paginas) => {
      const carregados = paginas.reduce((soma, p) => soma + p.itens.length, 0)
      return ultima.itens.length > 0 && carregados < ultima.total ? paginas.length : undefined
    },
    // Ao trocar o filtro a lista anterior fica na tela até a nova chegar: sem salto de
    // layout e com as opções do filtro de aba intactas.
    placeholderData: keepPreviousData,
    retry: false,
  })
}

/** "6 registros", "1 registro", "0 registros". */
export function contagemDeRegistros(total: number): string {
  return contagemDe(total, ['registro', 'registros'])
}

/** Quem fez: nome e cargo, ou "Sem sessão" quando não havia ninguém logado. */
export function quemFez(item: Pick<ItemDoHistorico, 'autor'>): {
  nome: string
  cargo: string | null
} {
  return item.autor === null ? { nome: 'Sem sessão', cargo: null } : item.autor
}

function textoDe(estado: Record<string, unknown> | null, chave: string): string | undefined {
  const valor = estado?.[chave]
  return typeof valor === 'string' ? valor : undefined
}

/**
 * As ações da senha e da indicação de e-mail. O histórico as guarda só para
 * consulta: o que mudou foi uma senha, um link ou um prazo, e isso não se
 * desfaz restaurando um estado anterior. A API já as marca como não
 * restauráveis; a tela repete a regra para nunca oferecer o contrário.
 */
export const ACOES_QUE_NAO_VOLTAM: ReadonlySet<string> = new Set([
  'redefinicao-de-senha',
  'troca-de-senha',
  'uso-do-codigo-de-recuperacao',
  'cadastro-de-email-de-recuperacao',
  'confirmacao-de-email-de-recuperacao',
  'remocao-de-email-de-recuperacao',
  'indicacao-de-email',
  'indicacao-cancelada',
  'indicacao-confirmada',
  'indicacao-concluida',
  'indicacao-expirada',
])

/** O nome do registro: o do estado novo e, sem ele (exclusão, revogação), o do anterior. */
function nomeDo(item: ItemDoHistorico): string {
  return textoDe(item.depois, 'nome') ?? textoDe(item.antes, 'nome') ?? 'sem nome'
}

/**
 * O local do tooltip, pelo nome que a tela de Conteúdos lhe dá. A linha gravada
 * antes de o histórico guardar o local não tem a chave `local`: nesse caso o
 * resumo fala só de "Tooltip".
 */
function localDo(item: ItemDoHistorico, nomes: ReadonlyMap<string, string>): string | undefined {
  for (const estado of [item.depois, item.antes]) {
    if (estado !== null && 'local' in estado) {
      const local = estado.local
      return rotuloDoLocal({ local: typeof local === 'string' ? local : null }, nomes)
    }
  }
  return undefined
}

/**
 * A frase do que mudou, montada aqui a partir do registro (e não gravada no
 * banco, para o texto poder melhorar sem migração). Ação que a tela não
 * conhece aparece pelo próprio código, e não some.
 */
export function resumoDoHistorico(
  item: ItemDoHistorico,
  nomesDosLocais: ReadonlyMap<string, string>
): string {
  const { acao } = item
  switch (item.aba) {
    case 'sessao':
      return (
        {
          entrada: 'Entrou na Área do Gestor.',
          'falha-de-entrada': 'Entrada recusada.',
          bloqueio: 'Acesso bloqueado por 15 minutos.',
          'troca-de-senha-inicial': 'Trocou a senha inicial.',
          'redefinicao-de-senha': 'Criou senha nova pelo link.',
          'troca-de-senha': 'Trocou a senha.',
          'uso-do-codigo-de-recuperacao': 'Usou o código de recuperação.',
          'cadastro-de-email-de-recuperacao': 'Cadastrou e-mail para recuperação.',
          'confirmacao-de-email-de-recuperacao': 'Confirmou o e-mail para recuperação.',
          'remocao-de-email-de-recuperacao': 'Removeu o e-mail para recuperação.',
        }[acao] ?? acao
      )
    case 'acessos': {
      const nome = nomeDo(item)
      return (
        {
          cadastro: `Cadastro de ${nome}.`,
          edicao: `Dados de ${nome} atualizados.`,
          revogacao: `Acesso de ${nome} revogado.`,
          restauracao: `Dados de ${nome} restaurados.`,
          'indicacao-de-email': `E-mail para o link de ${nome} indicado.`,
          'indicacao-cancelada': `Indicação de e-mail de ${nome} cancelada.`,
          'indicacao-confirmada': `Indicação de e-mail de ${nome} confirmada pela caixa nova.`,
          'indicacao-concluida': `Link de senha enviado ao e-mail indicado para ${nome}.`,
          'indicacao-expirada': `Indicação de e-mail de ${nome} expirou.`,
        }[acao] ?? acao
      )
    }
    case 'cargos':
      return acao === 'criacao' ? `Cargo ${nomeDo(item)} criado.` : acao
    case 'camadas': {
      const nome = nomeDo(item)
      return (
        {
          envio: `Camada ${nome} enviada para pendentes.`,
          teste: `Camada ${nome} testada no observatório.`,
          publicacao: `Camada ${nome} publicada.`,
          edicao: `Camada ${nome} atualizada.`,
          desativacao: `Camada ${nome} desativada.`,
          restauracao: `Camada ${nome} restaurada.`,
        }[acao] ?? acao
      )
    }
    case 'conteudos': {
      const local = localDo(item, nomesDosLocais)
      const de = local === undefined ? '' : ` de ${local}`
      return (
        {
          envio: 'Tooltip enviado para pendentes.',
          teste: 'Tooltip testado no observatório.',
          publicacao: `Tooltip${de} publicado.`,
          edicao: `Tooltip${de} atualizado.`,
          exclusao: `Tooltip${de} excluído.`,
          restauracao: `Tooltip${de} restaurado.`,
        }[acao] ?? acao
      )
    }
  }
}
